import { STORY } from '@constants';
import { StyledProps, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { OutboundLinkContext, TrackEvent } from '@enums';
import { AdditionalProductFragment } from '@graphql/generated/graphql';
import { useDisclosure } from '@hooks';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import StrapiImage from '@lib/image/strapiImage';
import { getStrapiAssetUrl } from '@lib/strapi';
import { Button, Card, CardProps, HStack, lineClamp, VStack } from '@lib/uikit';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo } from 'react';

import { AdditionalProductDetailTray } from './AdditionalProductDetailTray';

const SCard = styled(Card)`
    width: 100%;
    height: 100%;
    align-content: center;
    pointer-events: all;
`;

const SVStack = styled(VStack)`
    flex: 1;
    justify-content: space-between;
    padding-block: ${({ theme }) => theme.spacing.xxs};
`;

const SStrapiImage = styled(StrapiImage)`
    display: flex;
    width: 5rem;
    height: 5rem;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.shape.radius.sm};
`;

const Name = styled.p<StyledProps<{ color: string }>>`
    color: ${({ color }) => color};
    font: ${({ theme }) => theme.fonts.subtitle5};
    line-height: 1.2;
    font-weight: 900;
    ${lineClamp(1)}
`;

const Price = styled(Name)`
    font-weight: 500;
`;

interface Props extends CardProps {
    additionalProduct: AdditionalProductFragment;
    context: OutboundLinkContext;
    onDetailClose?: () => void;
    onDetailOpen?: () => void;
}

export const AdditionalProductBanner = ({
    additionalProduct,
    context,
    onDetailClose,
    onDetailOpen,
    ...cardProps
}: Props) => {
    const { track } = useAnalytics();
    const { palette } = useTheme();
    const { t } = useTranslation(STORY, {
        keyPrefix: 'additional_product.actions',
    });
    const color = useMemo(
        () =>
            cardProps.variant === 'blurred'
                ? palette.common.white
                : palette.common.black,
        [cardProps.variant, palette.common.black, palette.common.white]
    );
    const {
        isOpen: isDetailOpen,
        onClose: closeDetail,
        onOpen: openDetail,
    } = useDisclosure();

    const handleDetailOpen = useCallback(() => {
        openDetail();
        track(TrackEvent.AdditionalProductDetailOpen, {
            additional_product_id: additionalProduct.id,
        });
        onDetailOpen?.();
    }, [additionalProduct, onDetailOpen, openDetail, track]);

    const handleDetailClose = useCallback(() => {
        closeDetail();
        onDetailClose?.();
    }, [closeDetail, onDetailClose]);

    if (!additionalProduct.Url) return null;

    return (
        <>
            <SCard
                {...cardProps}
                cursor='pointer'
                padding='xxs'
                onClick={handleDetailOpen}
            >
                <HStack spacing='sm' alignItems='stretch'>
                    <SStrapiImage
                        sizes='5rem'
                        src={getStrapiAssetUrl(additionalProduct.Image)}
                        formats={additionalProduct.Image?.formats}
                        alt={
                            additionalProduct.Image?.alternativeText ??
                            additionalProduct.Name
                        }
                    />
                    <SVStack spacing='xxxs'>
                        <VStack spacing='xxxs'>
                            <Name color={color}>{additionalProduct.Name}</Name>
                            {additionalProduct.Price && (
                                <Price color={color}>
                                    {additionalProduct.Price}
                                </Price>
                            )}
                        </VStack>
                        <Button buttonSize='small' isFullWidth>
                            {t('add_to_cart')}
                        </Button>
                    </SVStack>
                </HStack>
            </SCard>
            <AdditionalProductDetailTray
                additionalProduct={additionalProduct}
                context={context}
                isOpen={isDetailOpen}
                onClose={handleDetailClose}
            />
        </>
    );
};
